declare const brand: unique symbol;

export type Branded<TValue, TName extends string> = TValue & {
  readonly [brand]: TName;
};

export type ClientId = Branded<string, "ClientId">;
export type UserId = Branded<string, "UserId">;
export type ProfessionalId = Branded<string, "ProfessionalId">;
export type CompanyId = Branded<string, "CompanyId">;
export type RoleId = Branded<string, "RoleId">;
export type PermissionId = Branded<string, "PermissionId">;
export type UserClientAccessId = Branded<string, "UserClientAccessId">;
export type AuditLogId = Branded<string, "AuditLogId">;
export type DocumentId = Branded<string, "DocumentId">;
export type DocumentRevisionId = Branded<string, "DocumentRevisionId">;
export type DocumentSnapshotId = Branded<string, "DocumentSnapshotId">;
export type DocumentSignatureId = Branded<string, "DocumentSignatureId">;
export type DocumentDeliveryId = Branded<string, "DocumentDeliveryId">;
export type DefinitionId = Branded<string, "DefinitionId">;
export type CorrelationId = Branded<string, "CorrelationId">;
export type SessionId = Branded<string, "SessionId">;
export type CoreCatalogId = Branded<string, "CoreCatalogId">;
export type CoreCatalogItemId = Branded<string, "CoreCatalogItemId">;
export type NormativeReferenceId = Branded<string, "NormativeReferenceId">;
export type ProjectId = Branded<string, "ProjectId">;
export type ProjectUnitAreaId = Branded<string, "ProjectUnitAreaId">;
export type AreaId = Branded<string, "AreaId">;
export type WorkOrderId = Branded<string, "WorkOrderId">;
export type ServiceFrontId = Branded<string, "ServiceFrontId">;
export type SignatoryId = Branded<string, "SignatoryId">;
export type SignatureId = Branded<string, "SignatureId">;
export type FormulaDefinitionId = Branded<string, "FormulaDefinitionId">;
export type FormulaVersionId = Branded<string, "FormulaVersionId">;
export type FormulaExecutionId = Branded<string, "FormulaExecutionId">;
export type ThicknessAlertId = Branded<string, "ThicknessAlertId">;
export type EquipmentId = Branded<string, "EquipmentId">;
export type InstrumentId = Branded<string, "InstrumentId">;
export type SafetyDeviceId = Branded<string, "SafetyDeviceId">;
export type InstrumentCalibrationId = Branded<string, "InstrumentCalibrationId">;
export type SafetyDeviceTestId = Branded<string, "SafetyDeviceTestId">;
export type UltrasonicEquipmentId = Branded<string, "UltrasonicEquipmentId">;
export type UltrasonicTransducerId = Branded<string, "UltrasonicTransducerId">;
export type CalibrationBlockId = Branded<string, "CalibrationBlockId">;

export type InspectionId = Branded<string, "InspectionId">;
export type ChecklistId = Branded<string, "ChecklistId">;
export type ChecklistComponentId = Branded<string, "ChecklistComponentId">;
export type ChecklistPhotoId = Branded<string, "ChecklistPhotoId">;
export type RecommendationId = Branded<string, "RecommendationId">;
export type IntegrityReportId = Branded<string, "IntegrityReportId">;
export type NdtReportId = Branded<string, "NdtReportId">;
export type MeasurementReportId = Branded<string, "MeasurementReportId">;
export type MeasurementPointId = Branded<string, "MeasurementPointId">;
export type MeasurementReadingId = Branded<string, "MeasurementReadingId">;
export type AvrId = Branded<string, "AvrId">;

export type RdoId = Branded<string, "RdoId">;
export type RdoConsolidatedId = Branded<string, "RdoConsolidatedId">;
export type DocumentAttachmentId = Branded<string, "DocumentAttachmentId">;
export type InspectionBookId = Branded<string, "InspectionBookId">;
export type ValidationIssueId = Branded<string, "ValidationIssueId">;
export type EngineerDecisionId = Branded<string, "EngineerDecisionId">;

export type DomainId =
  | ClientId
  | UserId
  | ProfessionalId
  | CompanyId
  | RoleId
  | PermissionId
  | UserClientAccessId
  | AuditLogId
  | DocumentId
  | DocumentRevisionId
  | DocumentSnapshotId
  | DocumentSignatureId
  | DocumentDeliveryId
  | DefinitionId
  | CorrelationId
  | SessionId
  | CoreCatalogId
  | CoreCatalogItemId
  | NormativeReferenceId
  | AreaId
  | WorkOrderId
  | ServiceFrontId
  | SignatoryId
  | SignatureId
  | FormulaDefinitionId
  | FormulaVersionId
  | FormulaExecutionId
  | ThicknessAlertId
  | EquipmentId
  | InstrumentId
  | SafetyDeviceId
  | InstrumentCalibrationId
  | SafetyDeviceTestId
  | UltrasonicEquipmentId
  | UltrasonicTransducerId
  | CalibrationBlockId
  | InspectionId
  | ChecklistId
  | ChecklistComponentId
  | ChecklistPhotoId
  | RecommendationId
  | IntegrityReportId
  | NdtReportId
  | MeasurementReportId
  | MeasurementPointId
  | MeasurementReadingId
  | AvrId
  | RdoId
  | RdoConsolidatedId
  | DocumentAttachmentId
  | InspectionBookId
  | ValidationIssueId
  | EngineerDecisionId
  | Branded<string, "StudioVersionId">
  | Branded<string, "StudioDependencyId">;

const UUID_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

// The generic return preserves the specific branded ID after runtime UUID validation.
 
export function domainId<TId extends DomainId>(value: string): TId {
  if (!UUID_PATTERN.test(value)) {
    throw new TypeError("Domain identifiers must be valid UUIDs");
  }

  return value as TId;
}
